"use client";

import React, { useRef, useEffect } from "react";
import styled from "styled-components";

import Box from "../Box";
import Flex from "../Flex";
import Icon from "../Icon";
import Text from "../Text";

import { positioningCSS, getBorder, getFont, getBorderRadius, getColor } from "../utils";
import { ElementPositioningProps } from "../ComponentProps";
import { ElementFlexProps } from "..";

type Props = {
  id?: string;
  value: string;
  label?: string;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  resize?: string;
  autoFocus?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  textareaStyle?: "success" | "warning" | "error";
  message?: string;
  overrideFontFamily?: string;
} & ElementPositioningProps &
  ElementFlexProps;

type ElementProps = Pick<Props, "resize" | "overrideFontFamily">;

const StyledTextArea = styled.textarea<ElementProps>`
  width: 100%;
  border: ${getBorder("1px solid border")};
  border-radius: ${props => getBorderRadius("default", null, props)};
  background: white;
  box-sizing: border-box;
  ${props => getFont("ui.regular", null, props)};
  ${positioningCSS({ px: "s", py: "s" })}
  resize: ${props => props.resize || "vertical"};
  &::placeholder {
    color: ${getColor("grayDark")};
  }
  &.active,
  &:focus {
    border: ${getBorder("1px solid focus")};
    outline: none;
  }
  &.error {
    border: ${getBorder("1px solid error")};
  }
  &.disabled {
    border: ${getBorder("1px solid border")};
    background: ${getColor("grayLighter")};
  }
`;

const Textarea: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  id,
  label,
  message,
  textareaStyle,
  maxLength,
  rows,
  resize = "vertical",
  disabled,
  readOnly,
  autoFocus,
  onFocus,
  onBlur,
  onSubmit,

  ...rest
}) => {
  const onKeyDown = onSubmit
    ? (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.metaKey && e.key === "Enter") {
          e.preventDefault();
          onSubmit();
        }
      }
    : undefined;

  const elementProps = {
    id,
    value,
    placeholder,
    maxLength,
    rows,
    resize,
    autoFocus,
    disabled,
    readOnly,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
  };

  const ref = useRef<HTMLTextAreaElement>();
  useEffect(() => {
    if (autoFocus) {
      ref.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getIcon() {
    switch (textareaStyle) {
      case "error":
        return <Icon size="16" fill="error" iconId="error" pr="xs" />;
      case "success":
        return <Icon size="16" fill="success" iconId="checkmark" pr="xs" />;
      case "warning":
        return <Icon size="16" fill="warning" iconId="warning" pr="xs" />;
      default:
        return null;
    }
  }

  return (
    <Box data-testid={id} width="100%" {...rest}>
      {label ? (
        <Text
          as="label"
          data-testid="textarea-label"
          display="block"
          color="black"
          mb="xs"
          htmlFor={id}>
          {label}
        </Text>
      ) : null}
      <Flex alignItems="center" width="100%">
        <StyledTextArea
          ref={ref}
          role="textbox"
          className={disabled || readOnly ? "disabled" : textareaStyle}
          {...elementProps}
        />
      </Flex>
      {message ? (
        <Flex data-testid="textarea-message" ml="s" flexDirection="row" alignItems="center">
          {getIcon()}
          <Text my="xs" font="ui.small" color={textareaStyle === "error" ? "error" : "black"}>
            {message}
          </Text>
        </Flex>
      ) : null}
    </Box>
  );
};

export default Textarea;
